import { Company } from '../types';

/** Máximo de tentativas de login antes do bloqueio client-side. */
export const MAX_LOGIN_ATTEMPTS = 5;

/** Duração do bloqueio após exceder tentativas (5 minutos). */
export const LOCKOUT_DURATION_MS = 5 * 60 * 1000;

/** Estado de tentativas por empresa — isolado para evitar lockout cruzado entre unidades. */
export interface LoginAttempt {
  count: number;
  lockoutUntil: number | null;
}

const attemptsByCompany = new Map<Company, LoginAttempt>();

/**
 * Lê tentativas armazenadas para a empresa.
 * @param company - Unidade selecionada no login
 * @returns Contador e timestamp de fim do bloqueio
 */
export function getStoredAttempts(company: Company): LoginAttempt {
  return attemptsByCompany.get(company) ?? { count: 0, lockoutUntil: null };
}

/**
 * Persiste tentativas de login por empresa.
 * @param company - Unidade selecionada
 * @param attempts - Estado atualizado
 */
export function setStoredAttempts(company: Company, attempts: LoginAttempt): void {
  attemptsByCompany.set(company, attempts);
}

/**
 * Verifica se a empresa está bloqueada por excesso de tentativas.
 * @param company - Unidade selecionada
 * @param now - Timestamp de referência (injetável em testes)
 * @returns true quando ainda dentro do período de bloqueio
 */
export function isLockedOut(company: Company, now: number = Date.now()): boolean {
  const attempts = getStoredAttempts(company);
  if (attempts.lockoutUntil && now < attempts.lockoutUntil) {
    return true;
  }
  if (attempts.lockoutUntil && now >= attempts.lockoutUntil) {
    setStoredAttempts(company, { count: 0, lockoutUntil: null });
    return false;
  }
  return false;
}

/**
 * Registra falha de login e aplica bloqueio após MAX_LOGIN_ATTEMPTS.
 * @param company - Unidade selecionada
 * @param now - Timestamp de referência (injetável em testes)
 */
export function recordFailedAttempt(company: Company, now: number = Date.now()): void {
  const attempts = getStoredAttempts(company);
  const newCount = attempts.count + 1;

  if (newCount >= MAX_LOGIN_ATTEMPTS) {
    setStoredAttempts(company, {
      count: newCount,
      lockoutUntil: now + LOCKOUT_DURATION_MS,
    });
  } else {
    setStoredAttempts(company, {
      count: newCount,
      lockoutUntil: null,
    });
  }
}

/**
 * Zera tentativas após login bem-sucedido.
 * @param company - Unidade selecionada
 */
export function recordSuccessfulAttempt(company: Company): void {
  setStoredAttempts(company, { count: 0, lockoutUntil: null });
}

/**
 * Retorna milissegundos restantes do bloqueio.
 * @param company - Unidade selecionada
 * @param now - Timestamp de referência (injetável em testes)
 * @returns Tempo restante em ms (0 se não bloqueado)
 */
export function getRemainingLockoutTime(company: Company, now: number = Date.now()): number {
  const attempts = getStoredAttempts(company);
  if (attempts.lockoutUntil) {
    return Math.max(0, attempts.lockoutUntil - now);
  }
  return 0;
}

/**
 * Limpa todo o estado de lockout — apenas para testes.
 */
export function clearAllLoginLockouts(): void {
  attemptsByCompany.clear();
}
